import {
  displayTitle,
  DRAFT_PACKAGE_IDS,
  DRAFT_PACKAGES,
  DRAFT_TYPE_LABEL,
  type DraftPackageId,
  DRAFTING_DAILY_LIMIT,
  type Matter,
  type MatterDraft,
  type MatterIntake,
} from '@buakei/types';
import { Badge, Button, EmptyState, ErrorState, LoadingState, Notice } from '@buakei/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { useId, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { AnswerView } from '@/assistant/ActsAssistant';
import { useAuth } from '@/auth/session';
import { useReaderTokens } from '@/pages/library/LibraryPages';
import { draftingApi } from '@/services/api';
import { saveFile, WORD_FILE, ZIP_FILE } from '@/services/save-file';
import { Page } from '@/shell/Page';
import { MatterDocuments } from './MatterDocuments';

/**
 * Drafting (ADR-037). The user describes a matter with placeholders instead of names; the
 * platform analyses the issues and law from the library and proposes documents; with the
 * user's go-ahead it drafts them; the user edits, asks for further work, and a lawyer approves.
 * In a Learning workspace the same flow is practice, never approved.
 */

export type Audience = 'professional' | 'learning';

const BASE: Record<Audience, string> = {
  professional: '/professional/drafting',
  learning: '/learning/drafting',
};

/** The active workspace where this account may draft (lawyer) or practise (student). */
function useDraftingWorkspace(audience: Audience) {
  const { session } = useAuth();
  const tokens = useReaderTokens();
  if (!tokens || !session?.activeWorkspaceId) return undefined;
  const workspace = session.workspaces.find((w) => w.id === session.activeWorkspaceId);
  const membership = session.memberships.find((m) => m.workspaceId === workspace?.id);
  if (!workspace || !membership) return undefined;
  const allowed =
    audience === 'learning'
      ? workspace.kind === 'learning' && membership.roles.includes('student')
      : workspace.kind !== 'learning' && membership.roles.includes('lawyer');
  return allowed ? { id: workspace.id, name: workspace.name } : undefined;
}

const keys = {
  list: (ws: string) => ['drafting', ws, 'list'] as const,
  matter: (ws: string, id: string) => ['drafting', ws, id] as const,
};

const STATUS: Record<MatterDraft['status'], { label: string; tone: 'plain' | 'success' }> = {
  ai_draft: { label: 'AI draft: not approved', tone: 'plain' },
  edited: { label: 'Edited: not approved', tone: 'plain' },
  approved: { label: 'Approved', tone: 'success' },
};

function NoWorkspace({ audience }: { audience: Audience }) {
  return (
    <EmptyState
      title={
        audience === 'professional'
          ? 'Drafting needs a lawyer role in a Professional workspace'
          : 'Practice drafting needs a Learning workspace'
      }
    >
      Choose a workspace where you have that role from the switcher at the top of the page.
    </EmptyState>
  );
}

function FactsNotice({ audience }: { audience: Audience }) {
  return (
    <Notice title="No real names or identifying details" icon={ShieldCheck}>
      Describe the matter with placeholders such as [Client], [Landlord] or [Property]. The facts
      are sent to the AI provider to analyse and draft
      {audience === 'professional'
        ? ', and Buakei AI is not yet cleared for confidential client information. Put the real names into the exported Word file yourself.'
        : '. Practice with invented facts.'}
    </Notice>
  );
}

export function DraftingHome({ audience }: { audience: Audience }) {
  const tokens = useReaderTokens();
  const workspace = useDraftingWorkspace(audience);
  const matters = useQuery({
    queryKey: keys.list(workspace?.id ?? ''),
    queryFn: () => draftingApi.list(tokens!, workspace!.id),
    enabled: Boolean(tokens && workspace),
  });
  const title = audience === 'professional' ? 'Drafting' : 'Practice drafting';
  return (
    <Page
      title={title}
      description={
        audience === 'professional'
          ? 'Describe a matter; Buakei AI works out the issues and the law from the library, proposes the documents, and drafts the ones you choose for you to edit and approve.'
          : 'Practise drafting on invented facts: see the issues, the law and the documents a matter needs, then draft and improve them.'
      }
      width="wide"
      actions={
        workspace ? (
          <Link
            to={`${BASE[audience]}/new`}
            className="inline-flex h-10 items-center gap-2 rounded-md bg-navy-900 px-4 text-sm font-medium text-white hover:bg-navy-700"
          >
            <Plus className="size-4" aria-hidden /> New matter
          </Link>
        ) : null
      }
    >
      {!workspace ? <NoWorkspace audience={audience} /> : null}
      {workspace && matters.isPending ? <LoadingState label="Loading matters" /> : null}
      {matters.isError ? (
        <ErrorState title="Matters could not be loaded" onRetry={() => void matters.refetch()}>
          {matters.error.message}
        </ErrorState>
      ) : null}
      {matters.data && matters.data.matters.length === 0 ? (
        <EmptyState title="No matters yet">
          Start a new matter: describe the facts and what the client wants.
        </EmptyState>
      ) : null}
      {matters.data && matters.data.matters.length > 0 ? (
        <ul className="flex flex-col divide-y divide-line-200 rounded-lg border border-line-200 bg-white">
          {matters.data.matters.map((m) => (
            <li key={m.id} className="flex flex-col gap-1 p-4">
              <Link
                to={`${BASE[audience]}/${m.id}`}
                className="font-medium text-navy-900 underline-offset-2 hover:underline"
              >
                {m.title}
              </Link>
              <span className="text-sm text-muted-600">
                {m.isOwner
                  ? m.sharedWithWorkspace
                    ? 'Shared with the firm · '
                    : ''
                  : `Shared by ${m.ownerName ?? 'a colleague'} · `}
                {m.matterType} · {m.planned ? 'Analysed' : 'Not analysed yet'} · {m.drafts}{' '}
                {m.drafts === 1 ? 'draft' : 'drafts'} · updated{' '}
                {new Date(m.updatedAt).toLocaleDateString('en-GB')}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </Page>
  );
}

/** Capitalised word pairs outside square brackets that may be real names (a hint only). */
const NOT_NAMES =
  /\b(?:Act|Acts|Court|High|Supreme|Appeal|Sierra|Leone|Freetown|Republic|Government|Ministry|Bank|Limited|Company|Section|Constitution|Rules|Police|Council|Republic|January|February|March|April|May|June|July|August|September|October|November|December)\b/;
export function possibleNames(text: string): string[] {
  const outside = text.replace(/\[[^\]]*\]/g, ' ');
  const found = new Set<string>();
  for (const m of outside.matchAll(/\b[A-Z][a-z]{2,}(?:\s+[A-Z][a-z]{2,})+\b/g)) {
    if (!NOT_NAMES.test(m[0])) found.add(m[0]);
  }
  return [...found].slice(0, 6);
}

const field = 'rounded-md border border-line-200 bg-white p-2 text-ink-900';

export function NewMatterPage({ audience }: { audience: Audience }) {
  const tokens = useReaderTokens();
  const workspace = useDraftingWorkspace(audience);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const id = useId();
  const [form, setForm] = useState({
    title: '',
    matterType: '',
    acting: '',
    facts: '',
    objective: '',
  });
  const [parties, setParties] = useState([
    { label: '[Client]', role: '' },
    { label: '', role: '' },
  ]);
  const [confirmed, setConfirmed] = useState(false);
  const create = useMutation({
    mutationFn: () => {
      const intake: MatterIntake = {
        ...form,
        parties: parties.filter((p) => p.label.trim() || p.role.trim()),
        anonymised: true,
      };
      return draftingApi.create(tokens!, workspace!.id, intake);
    },
    onSuccess: (matter) => {
      void queryClient.invalidateQueries({ queryKey: keys.list(workspace!.id) });
      void navigate(`${BASE[audience]}/${matter.id}`);
    },
  });
  if (!workspace) {
    return (
      <Page title="New matter">
        <NoWorkspace audience={audience} />
      </Page>
    );
  }
  const names = possibleNames(
    [form.title, form.acting, form.facts, form.objective, ...parties.map((p) => p.role)].join(' '),
  );
  const badLabels = parties.filter(
    (p) => p.label.trim() && !/^\[[^\]]{1,40}\]$/.test(p.label.trim()),
  );
  const ready =
    confirmed &&
    form.title.trim().length >= 3 &&
    form.matterType.trim().length >= 3 &&
    form.acting.trim().length >= 2 &&
    form.facts.trim().length >= 40 &&
    form.objective.trim().length >= 5 &&
    parties.some((p) => p.label.trim() && p.role.trim().length >= 2) &&
    badLabels.length === 0;
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [k]: e.target.value });
  return (
    <Page
      title="New matter"
      description="Describe the matter. Nothing is sent to the AI until you ask for the analysis."
    >
      <Link
        to={BASE[audience]}
        className="flex items-center gap-1 self-start text-sm font-medium text-navy-900 underline"
      >
        <ArrowLeft className="size-4" aria-hidden /> All matters
      </Link>
      <FactsNotice audience={audience} />
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) create.mutate();
        }}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1">
            <label htmlFor={`${id}-title`} className="text-sm font-medium text-ink-900">
              Title for your list
            </label>
            <input
              id={`${id}-title`}
              value={form.title}
              onChange={set('title')}
              maxLength={120}
              placeholder="Notice to quit, [Client]"
              className={field}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor={`${id}-type`} className="text-sm font-medium text-ink-900">
              Kind of matter
            </label>
            <input
              id={`${id}-type`}
              value={form.matterType}
              onChange={set('matterType')}
              maxLength={120}
              placeholder="Tenancy dispute, sale of land, unpaid debt…"
              className={field}
            />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={`${id}-acting`} className="text-sm font-medium text-ink-900">
            Acting for
          </label>
          <input
            id={`${id}-acting`}
            value={form.acting}
            onChange={set('acting')}
            maxLength={300}
            placeholder="The tenant, [Client]"
            className={field}
          />
        </div>
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium text-ink-900">
            Parties (placeholders in square brackets)
          </legend>
          {parties.map((p, i) => (
            <div key={i} className="grid gap-2 md:grid-cols-[14rem_minmax(0,1fr)_auto]">
              <input
                aria-label={`Party ${i + 1} placeholder`}
                value={p.label}
                onChange={(e) =>
                  setParties(parties.map((q, j) => (j === i ? { ...q, label: e.target.value } : q)))
                }
                placeholder="[Landlord]"
                maxLength={42}
                className={field}
              />
              <input
                aria-label={`Party ${i + 1} role`}
                value={p.role}
                onChange={(e) =>
                  setParties(parties.map((q, j) => (j === i ? { ...q, role: e.target.value } : q)))
                }
                placeholder="Landlord of the property"
                maxLength={160}
                className={field}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setParties(parties.filter((_, j) => j !== i))}
                disabled={parties.length <= 1}
              >
                Remove
              </Button>
            </div>
          ))}
          {parties.length < 10 ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="self-start"
              onClick={() => setParties([...parties, { label: '', role: '' }])}
            >
              Add a party
            </Button>
          ) : null}
          {badLabels.length > 0 ? (
            <p className="text-sm text-danger-700">
              Write each party as a placeholder in square brackets, such as [Buyer].
            </p>
          ) : null}
        </fieldset>
        <div className="flex flex-col gap-1">
          <label htmlFor={`${id}-facts`} className="text-sm font-medium text-ink-900">
            Facts
          </label>
          <textarea
            id={`${id}-facts`}
            rows={8}
            value={form.facts}
            onChange={set('facts')}
            maxLength={8000}
            placeholder="What happened, when, and what documents exist. Use [date], [amount] and party placeholders."
            className={field}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={`${id}-objective`} className="text-sm font-medium text-ink-900">
            What the client wants
          </label>
          <textarea
            id={`${id}-objective`}
            rows={2}
            value={form.objective}
            onChange={set('objective')}
            maxLength={2000}
            className={field}
          />
        </div>
        {names.length > 0 ? (
          <p role="status" className="rounded-md bg-navy-50 px-3 py-2 text-sm text-ink-900">
            These may be real names: {names.join(', ')}. If they are, replace them with
            placeholders.
          </p>
        ) : null}
        <label className="flex items-start gap-2 text-sm text-ink-900">
          <input
            type="checkbox"
            className="mt-1"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          />
          <span>
            I have used placeholders and included no real names, contact details or other
            identifying information.
          </span>
        </label>
        <Button type="submit" className="self-start" disabled={!ready || create.isPending}>
          {create.isPending ? 'Saving…' : 'Save matter'}
        </Button>
        {create.isError ? (
          <p role="alert" className="text-sm text-ink-900">
            {create.error.message}
          </p>
        ) : null}
      </form>
    </Page>
  );
}

function useMatter(audience: Audience) {
  const tokens = useReaderTokens();
  const workspace = useDraftingWorkspace(audience);
  const { matterId = '' } = useParams();
  const matter = useQuery({
    queryKey: keys.matter(workspace?.id ?? '', matterId),
    queryFn: () => draftingApi.get(tokens!, workspace!.id, matterId),
    enabled: Boolean(tokens && workspace),
  });
  return { tokens, workspace, matterId, matter };
}

function Remaining({ matter }: { matter: Matter }) {
  return matter.remainingToday === null ? null : (
    <span className="text-sm text-muted-600">
      {matter.remainingToday} of {DRAFTING_DAILY_LIMIT} drafting tasks left today
    </span>
  );
}

export function MatterPage({ audience }: { audience: Audience }) {
  const { tokens, workspace, matterId, matter } = useMatter(audience);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [chosen, setChosen] = useState<string[] | null>(null);
  const [directions, setDirections] = useState('');
  const [progress, setProgress] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const id = useId();
  const update = (m: Matter) => queryClient.setQueryData(keys.matter(workspace!.id, matterId), m);
  const plan = useMutation({
    mutationFn: () => draftingApi.plan(tokens!, workspace!.id, matterId),
    onSuccess: (m) => {
      update(m);
      setChosen(null);
    },
  });
  const draft = useMutation({
    mutationFn: async (documentIds: string[]) => {
      let latest: Matter | null = null;
      for (const [i, documentId] of documentIds.entries()) {
        setProgress(`Drafting ${i + 1} of ${documentIds.length}…`);
        latest = await draftingApi.draft(
          tokens!,
          workspace!.id,
          matterId,
          documentId,
          directions.trim() || undefined,
        );
        update(latest);
      }
      return latest;
    },
    onSettled: () => setProgress(null),
  });
  const share = useMutation({
    mutationFn: (shared: boolean) => draftingApi.share(tokens!, workspace!.id, matterId, shared),
    onSuccess: update,
  });
  const remove = useMutation({
    mutationFn: () => draftingApi.remove(tokens!, workspace!.id, matterId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.list(workspace!.id) });
      void navigate(BASE[audience]);
    },
  });
  if (!workspace) {
    return (
      <Page title="Matter">
        <NoWorkspace audience={audience} />
      </Page>
    );
  }
  const m = matter.data;
  const drafted = new Set(m?.drafts.map((d) => d.planId) ?? []);
  const selectable = m?.plan?.documents.filter((d) => !d.blockedReason && !drafted.has(d.id)) ?? [];
  const selected = chosen ?? selectable.filter((d) => d.recommended).map((d) => d.id);
  return (
    <Page title={m?.title ?? 'Matter'} width="wide">
      <Link
        to={BASE[audience]}
        className="flex items-center gap-1 self-start text-sm font-medium text-navy-900 underline"
      >
        <ArrowLeft className="size-4" aria-hidden /> All matters
      </Link>
      {matter.isPending ? <LoadingState label="Loading the matter" /> : null}
      {matter.isError ? (
        <ErrorState title="This matter could not be loaded" onRetry={() => void matter.refetch()}>
          {matter.error.message}
        </ErrorState>
      ) : null}
      {m ? (
        <div className="flex flex-col gap-8">
          {m.practice ? <Badge>Practice: not for use in a real matter</Badge> : null}
          {!m.isOwner ? (
            <p className="text-sm text-ink-900">
              Shared by {m.ownerName ?? 'a colleague'} with the firm. You can review, edit, ask for
              further work and approve its drafts.
            </p>
          ) : null}
          {m.canShare ? (
            <label className="flex items-start gap-2 rounded-md border border-line-200 bg-white p-3 text-sm text-ink-900">
              <input
                type="checkbox"
                className="mt-1"
                checked={m.sharedWithWorkspace}
                disabled={share.isPending}
                onChange={(e) => share.mutate(e.target.checked)}
              />
              <span>
                <strong>Share with {workspace.name}.</strong> The firm&apos;s other lawyers can open
                this matter, edit and revise its drafts, and approve them. Only you can delete it or
                stop sharing.
              </span>
            </label>
          ) : null}
          {share.isError ? (
            <p role="alert" className="text-sm text-ink-900">
              {share.error.message}
            </p>
          ) : null}
          <details className="rounded-lg border border-line-200 bg-white p-4">
            <summary className="cursor-pointer font-medium text-navy-900">
              The matter as described
            </summary>
            <dl className="mt-3 grid gap-2 text-sm text-ink-900 md:grid-cols-[10rem_minmax(0,1fr)]">
              <dt className="font-medium">Kind</dt>
              <dd>{m.intake.matterType}</dd>
              <dt className="font-medium">Acting for</dt>
              <dd>{m.intake.acting}</dd>
              <dt className="font-medium">Parties</dt>
              <dd>{m.intake.parties.map((p) => `${p.label} (${p.role})`).join('; ')}</dd>
              <dt className="font-medium">Facts</dt>
              <dd className="whitespace-pre-wrap">{m.intake.facts}</dd>
              <dt className="font-medium">Wants</dt>
              <dd>{m.intake.objective}</dd>
            </dl>
          </details>

          {m.documentsEnabled ? (
            <MatterDocuments tokens={tokens!} workspaceId={workspace.id} matter={m} />
          ) : null}

          <section aria-labelledby="step1" className="flex flex-col gap-3">
            <h2 id="step1" className="font-serif text-xl font-semibold text-navy-900">
              1. Issues and the law
            </h2>
            {!m.plan ? (
              <p className="text-sm text-ink-900">
                Buakei AI will identify the legal issues, find the applicable law in the library and
                propose the documents this matter needs. The facts above are sent to the AI
                provider. It uses one of today&apos;s {DRAFTING_DAILY_LIMIT} drafting tasks.
              </p>
            ) : null}
            <div className="flex flex-wrap items-center gap-3">
              <Button
                onClick={() => plan.mutate()}
                disabled={plan.isPending || draft.isPending}
                variant={m.plan ? 'secondary' : 'primary'}
              >
                {plan.isPending
                  ? 'Analysing… (this can take a minute)'
                  : m.plan
                    ? 'Analyse again'
                    : 'Analyse the matter'}
              </Button>
              <Remaining matter={m} />
            </div>
            {plan.isError ? (
              <p role="alert" className="text-sm text-ink-900">
                {plan.error.message}
              </p>
            ) : null}
            {m.plan ? (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <h3 className="text-sm font-semibold text-navy-900">Issues</h3>
                  <ul className="flex flex-col gap-1 pl-5 text-sm text-ink-900">
                    {m.plan.issues.map((i) => (
                      <li key={i.issue} className="list-disc">
                        <strong>{i.issue}.</strong> {i.note}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-lg border border-line-200 bg-white p-4">
                  <h3 className="mb-3 text-sm font-semibold text-navy-900">
                    The law, from the library
                  </h3>
                  <AnswerView answer={m.plan.analysis} turnId={1} />
                </div>
                {m.plan.missing.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    <h3 className="text-sm font-semibold text-navy-900">
                      Information still needed
                    </h3>
                    <ul className="flex flex-col gap-1 pl-5 text-sm text-ink-900">
                      {m.plan.missing.map((x) => (
                        <li key={x} className="list-disc">
                          {x}
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-muted-600">
                      Drafts leave placeholders in square brackets where these are needed.
                    </p>
                  </div>
                ) : null}
              </div>
            ) : null}
          </section>

          {m.plan ? (
            <section aria-labelledby="step2" className="flex flex-col gap-3">
              <h2 id="step2" className="font-serif text-xl font-semibold text-navy-900">
                2. Documents to draft
              </h2>
              <ul className="flex flex-col gap-2">
                {m.plan.documents.map((d) => {
                  const done = drafted.has(d.id);
                  return (
                    <li
                      key={d.id}
                      className="flex items-start gap-3 rounded-md border border-line-200 bg-white p-3"
                    >
                      <input
                        type="checkbox"
                        id={`${id}-${d.id}`}
                        className="mt-1"
                        disabled={Boolean(d.blockedReason) || done || draft.isPending}
                        checked={!d.blockedReason && !done && selected.includes(d.id)}
                        onChange={(e) =>
                          setChosen(
                            e.target.checked
                              ? [...selected, d.id]
                              : selected.filter((x) => x !== d.id),
                          )
                        }
                      />
                      <label htmlFor={`${id}-${d.id}`} className="flex flex-col gap-1 text-sm">
                        <span className="font-medium text-navy-900">
                          {d.title}{' '}
                          <span className="font-normal text-muted-600">
                            · {DRAFT_TYPE_LABEL[d.type]}
                          </span>
                        </span>
                        <span className="text-ink-900">{d.purpose}</span>
                        {d.recommended ? (
                          <span className="text-xs text-muted-600">Recommended now</span>
                        ) : null}
                        {done ? <span className="text-xs text-ok-700">Drafted</span> : null}
                        {d.blockedReason ? (
                          <span className="text-xs text-danger-700">{d.blockedReason}</span>
                        ) : null}
                      </label>
                    </li>
                  );
                })}
              </ul>
              {selectable.length > 0 ? (
                <div className="flex flex-col gap-2">
                  <label htmlFor={`${id}-directions`} className="text-sm font-medium text-ink-900">
                    Directions for the drafts (optional)
                  </label>
                  <textarea
                    id={`${id}-directions`}
                    rows={2}
                    maxLength={2000}
                    value={directions}
                    onChange={(e) => setDirections(e.target.value)}
                    placeholder="For example: firm but courteous tone; give 14 days."
                    className={field}
                  />
                  <p className="text-sm text-ink-900">
                    Drafting sends the facts and the analysis to the AI provider. Each document uses
                    one drafting task.
                  </p>
                  <div className="flex flex-wrap items-center gap-3">
                    <Button
                      onClick={() => draft.mutate(selected)}
                      disabled={selected.length === 0 || draft.isPending || plan.isPending}
                    >
                      {draft.isPending
                        ? (progress ?? 'Drafting…')
                        : `Draft ${selected.length} ${selected.length === 1 ? 'document' : 'documents'}`}
                    </Button>
                  </div>
                  {draft.isError ? (
                    <p role="alert" className="text-sm text-ink-900">
                      {draft.error.message}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </section>
          ) : null}

          {m.plan ? (
            <FilingPackages
              tokens={tokens!}
              workspaceId={workspace.id}
              matter={m}
              audience={audience}
              onUpdate={update}
            />
          ) : null}

          {m.drafts.length > 0 ? (
            <section aria-labelledby="step3" className="flex flex-col gap-3">
              <h2 id="step3" className="font-serif text-xl font-semibold text-navy-900">
                3. Drafts
              </h2>
              <ul className="flex flex-col divide-y divide-line-200 rounded-lg border border-line-200 bg-white">
                {m.drafts.map((d) => (
                  <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 p-4">
                    <Link
                      to={`${BASE[audience]}/${m.id}/drafts/${d.id}`}
                      className="font-medium text-navy-900 underline-offset-2 hover:underline"
                    >
                      {d.title}
                    </Link>
                    <span className="flex items-center gap-2 text-sm text-muted-600">
                      {DRAFT_TYPE_LABEL[d.type]} · version {d.currentVersion}
                      <Badge tone={STATUS[d.status].tone}>
                        {m.practice ? 'Practice draft' : STATUS[d.status].label}
                      </Badge>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {m.isOwner ? (
            <div className="flex flex-wrap items-center gap-3 border-t border-line-200 pt-4">
              {confirmDelete ? (
                <>
                  <span className="text-sm text-ink-900">
                    Delete this matter and all its drafts?
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => remove.mutate()}
                    disabled={remove.isPending}
                  >
                    Delete permanently
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}>
                    Keep it
                  </Button>
                </>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(true)}>
                  <Trash2 className="size-4" aria-hidden /> Delete matter
                </Button>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </Page>
  );
}

/**
 * Filing packages (ADR-040): the documents that start proceedings, added together, drafted
 * in order so each stays consistent with the ones before it, and exported as one zip.
 */
function FilingPackages({
  tokens,
  workspaceId,
  matter,
  audience,
  onUpdate,
}: {
  tokens: NonNullable<ReturnType<typeof useReaderTokens>>;
  workspaceId: string;
  matter: Matter;
  audience: Audience;
  onUpdate: (m: Matter) => void;
}) {
  const [progress, setProgress] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: (pkg: DraftPackageId) =>
      draftingApi.addPackage(tokens, workspaceId, matter.id, pkg),
    onSuccess: onUpdate,
  });
  const draftAll = useMutation({
    mutationFn: async (pkg: DraftPackageId) => {
      const todo = (matter.plan?.documents ?? []).filter(
        (d) => d.packageId === pkg && !matter.drafts.some((x) => x.planId === d.id),
      );
      for (const [i, d] of todo.entries()) {
        setProgress(`Drafting ${i + 1} of ${todo.length}: ${d.title}…`);
        onUpdate(await draftingApi.draft(tokens, workspaceId, matter.id, d.id));
      }
    },
    onSettled: () => setProgress(null),
  });
  const exportZip = useMutation({
    mutationFn: async (pkg: DraftPackageId) =>
      saveFile(
        await draftingApi.exportPackage(tokens, workspaceId, matter.id, pkg),
        `${matter.title} - ${DRAFT_PACKAGES[pkg].label}`,
        ZIP_FILE,
      ),
  });
  const busy = add.isPending || draftAll.isPending;
  const error = add.error ?? draftAll.error ?? exportZip.error;
  return (
    <section aria-labelledby="packages-h" className="flex flex-col gap-3">
      <h2 id="packages-h" className="font-serif text-xl font-semibold text-navy-900">
        Filing packages
      </h2>
      <p className="text-sm text-ink-900">
        The documents that start court proceedings, drafted together so the parties, claims and
        relief stay consistent, then exported as one zip. Each document uses one drafting task.
      </p>
      <ul className="flex flex-col gap-3">
        {DRAFT_PACKAGE_IDS.map((pkg) => {
          const def = DRAFT_PACKAGES[pkg];
          const docs = (matter.plan?.documents ?? []).filter((d) => d.packageId === pkg);
          const drafted = docs.filter((d) => matter.drafts.some((x) => x.planId === d.id));
          return (
            <li
              key={pkg}
              className="flex flex-col gap-2 rounded-md border border-line-200 bg-white p-4"
            >
              <span className="font-medium text-navy-900">{def.label}</span>
              <span className="text-sm text-ink-900">{def.description}</span>
              {docs.length === 0 ? (
                <Button
                  variant="secondary"
                  size="sm"
                  className="self-start"
                  disabled={busy}
                  onClick={() => add.mutate(pkg)}
                  aria-label={`Add the package: ${def.label}`}
                >
                  Add this package
                </Button>
              ) : (
                <>
                  <ol className="flex list-decimal flex-col gap-1 pl-5 text-sm text-ink-900">
                    {docs.map((d) => {
                      const draft = matter.drafts.find((x) => x.planId === d.id);
                      return (
                        <li key={d.id}>
                          {draft ? (
                            <Link
                              to={`${BASE[audience]}/${matter.id}/drafts/${draft.id}`}
                              className="font-medium text-navy-900 underline-offset-2 hover:underline"
                            >
                              {d.title}
                            </Link>
                          ) : (
                            d.title
                          )}{' '}
                          <span className="text-muted-600">
                            ·{' '}
                            {draft
                              ? matter.practice
                                ? 'practice draft'
                                : STATUS[draft.status].label
                              : 'not drafted yet'}
                          </span>
                        </li>
                      );
                    })}
                  </ol>
                  <div className="flex flex-wrap items-center gap-3">
                    {drafted.length < docs.length ? (
                      <Button
                        size="sm"
                        disabled={busy}
                        onClick={() => draftAll.mutate(pkg)}
                        aria-label={`Draft the package: ${def.label}`}
                      >
                        {draftAll.isPending && draftAll.variables === pkg
                          ? (progress ?? 'Drafting…')
                          : `Draft ${docs.length - drafted.length === docs.length ? 'the package' : 'the rest'}`}
                      </Button>
                    ) : null}
                    {drafted.length > 0 ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={exportZip.isPending}
                        onClick={() => exportZip.mutate(pkg)}
                        aria-label={`Export the package: ${def.label}`}
                      >
                        <Download className="size-4" aria-hidden /> Export the package (.zip)
                      </Button>
                    ) : null}
                  </div>
                </>
              )}
            </li>
          );
        })}
      </ul>
      {error ? (
        <p role="alert" className="text-sm text-ink-900">
          {error.message}
        </p>
      ) : null}
    </section>
  );
}

export function DraftPage({ audience }: { audience: Audience }) {
  const { tokens, workspace, matterId, matter } = useMatter(audience);
  const { draftId = '' } = useParams();
  const queryClient = useQueryClient();
  const id = useId();
  const [shown, setShown] = useState<number | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [instruction, setInstruction] = useState('');
  const update = (m: Matter) => {
    queryClient.setQueryData(keys.matter(workspace!.id, matterId), m);
    setText(null);
    setShown(null);
  };
  const draft = matter.data?.drafts.find((d) => d.id === draftId);
  const edit = useMutation({
    mutationFn: () =>
      draftingApi.edit(tokens!, workspace!.id, matterId, draftId, text!, draft!.currentVersion),
    onSuccess: update,
  });
  const revise = useMutation({
    mutationFn: () =>
      draftingApi.revise(
        tokens!,
        workspace!.id,
        matterId,
        draftId,
        instruction.trim(),
        draft!.currentVersion,
      ),
    onSuccess: (m) => {
      update(m);
      setInstruction('');
    },
  });
  const approve = useMutation({
    mutationFn: () =>
      draftingApi.approve(tokens!, workspace!.id, matterId, draftId, draft!.currentVersion),
    onSuccess: update,
  });
  const exportWord = useMutation({
    mutationFn: async () =>
      saveFile(
        await draftingApi.exportDocx(tokens!, workspace!.id, matterId, draftId),
        draft!.title,
        WORD_FILE,
      ),
  });
  if (!workspace) {
    return (
      <Page title="Draft">
        <NoWorkspace audience={audience} />
      </Page>
    );
  }
  const m = matter.data;
  const version = draft?.versions.find((v) => v.version === (shown ?? draft.currentVersion));
  const isCurrent = version?.version === draft?.currentVersion;
  const body = isCurrent && text !== null ? text : (version?.body ?? '');
  const placeholders = [...new Set(body.match(/\[[^\]\d][^\]]*\]/g) ?? [])];
  const busy = edit.isPending || revise.isPending || approve.isPending;
  return (
    <Page title={draft?.title ?? 'Draft'} width="wide">
      <Link
        to={`${BASE[audience]}/${matterId}`}
        className="flex items-center gap-1 self-start text-sm font-medium text-navy-900 underline"
      >
        <ArrowLeft className="size-4" aria-hidden /> Back to the matter
      </Link>
      {matter.isPending ? <LoadingState label="Loading the draft" /> : null}
      {matter.isError ? (
        <ErrorState title="This draft could not be loaded" onRetry={() => void matter.refetch()}>
          {matter.error.message}
        </ErrorState>
      ) : null}
      {m && !draft ? <EmptyState title="This draft was not found" /> : null}
      {m && draft && version ? (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_18rem]">
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <Badge tone={STATUS[draft.status].tone}>
                {m.practice ? 'Practice draft' : STATUS[draft.status].label}
              </Badge>
              <span className="text-sm text-muted-600">
                {DRAFT_TYPE_LABEL[draft.type]} · version {version.version}
                {isCurrent ? ' (current)' : ''}
              </span>
            </div>
            {draft.approval && !m.practice ? (
              <Notice title={`Approved by ${draft.approval.byName ?? 'a lawyer'}`}>
                Version {draft.approval.version}, on{' '}
                {new Date(draft.approval.at).toLocaleDateString('en-GB')}. Any change makes it a
                draft again.
              </Notice>
            ) : (
              <Notice title={m.practice ? 'Practice draft' : 'Not approved'}>
                {m.practice
                  ? 'Written for learning. Check every authority against the library.'
                  : 'An AI draft until a lawyer approves it. Do not send, file or rely on it before then. Check every authority against its source.'}
              </Notice>
            )}
            <label htmlFor={`${id}-body`} className="sr-only">
              Draft text
            </label>
            <textarea
              id={`${id}-body`}
              rows={24}
              value={body}
              readOnly={!isCurrent}
              onChange={(e) => setText(e.target.value)}
              className="w-full rounded-md border border-line-200 bg-white p-4 font-serif leading-relaxed text-ink-900"
            />
            {isCurrent ? (
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  onClick={() => edit.mutate()}
                  disabled={
                    text === null || text === version.body || text.trim().length < 20 || busy
                  }
                >
                  {edit.isPending ? 'Saving…' : 'Save my changes'}
                </Button>
                {text !== null && text !== version.body ? (
                  <Button variant="ghost" onClick={() => setText(null)}>
                    Discard changes
                  </Button>
                ) : null}
              </div>
            ) : (
              <Button variant="secondary" className="self-start" onClick={() => setShown(null)}>
                Back to the current version
              </Button>
            )}
            {edit.isError ? (
              <p role="alert" className="text-sm text-ink-900">
                {edit.error.message}
              </p>
            ) : null}
            {placeholders.length > 0 ? (
              <p className="text-sm text-ink-900">
                <strong>To complete before use:</strong> {placeholders.slice(0, 20).join(', ')}
              </p>
            ) : null}
            <section
              aria-labelledby={`${id}-more`}
              className="flex flex-col gap-2 rounded-lg border border-line-200 bg-soft-white p-4"
            >
              <h2 id={`${id}-more`} className="text-sm font-semibold text-navy-900">
                Ask for further work
              </h2>
              <label htmlFor={`${id}-instruction`} className="text-sm text-ink-900">
                What should change? The current version is revised and kept as a new version.
              </label>
              <textarea
                id={`${id}-instruction`}
                rows={3}
                maxLength={2000}
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                placeholder="For example: add a clause on repairs; make the demand 14 days; address the defence of waiver."
                className={field}
              />
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  onClick={() => revise.mutate()}
                  disabled={
                    instruction.trim().length < 5 ||
                    (text !== null && text !== version.body) ||
                    busy
                  }
                >
                  {revise.isPending ? 'Revising… (this can take a minute)' : 'Revise the draft'}
                </Button>
                <Remaining matter={m} />
              </div>
              {text !== null && text !== version.body ? (
                <p className="text-xs text-muted-600">Save or discard your changes first.</p>
              ) : null}
              {revise.isError ? (
                <p role="alert" className="text-sm text-ink-900">
                  {revise.error.message}
                </p>
              ) : null}
            </section>
          </div>
          <aside aria-label="Draft actions" className="flex flex-col gap-5">
            {m.canApprove && isCurrent && draft.status !== 'approved' ? (
              <div className="flex flex-col gap-2">
                <Button
                  onClick={() => approve.mutate()}
                  disabled={busy || (text !== null && text !== version.body)}
                >
                  Approve this version
                </Button>
                <p className="text-xs text-muted-600">
                  You confirm, as the responsible lawyer, that you have reviewed this version and
                  its authorities.
                </p>
                {approve.isError ? (
                  <p role="alert" className="text-sm text-ink-900">
                    {approve.error.message}
                  </p>
                ) : null}
              </div>
            ) : null}
            <Button
              variant="secondary"
              onClick={() => exportWord.mutate()}
              disabled={exportWord.isPending}
            >
              <Download className="size-4" aria-hidden />{' '}
              {exportWord.isPending ? 'Preparing…' : 'Export to Word'}
            </Button>
            {exportWord.isError ? (
              <p role="alert" className="text-sm text-ink-900">
                {exportWord.error.message}
              </p>
            ) : null}
            <div className="flex flex-col gap-2">
              <h2 className="text-sm font-semibold text-navy-900">Versions</h2>
              <ol className="flex flex-col gap-1 text-sm">
                {[...draft.versions].reverse().map((v) => (
                  <li key={v.version}>
                    <button
                      type="button"
                      onClick={() =>
                        setShown(v.version === draft.currentVersion ? null : v.version)
                      }
                      aria-current={v.version === version.version ? 'true' : undefined}
                      className="text-left text-navy-900 underline-offset-2 hover:underline aria-[current]:font-semibold"
                    >
                      Version {v.version}:{' '}
                      {v.kind === 'ai_draft'
                        ? 'AI draft'
                        : v.kind === 'revision'
                          ? 'AI revision'
                          : 'edited'}
                      {draft.approval?.version === v.version ? ' · approved' : ''}
                    </button>
                    {v.instruction ? (
                      <span className="block text-xs text-muted-600">“{v.instruction}”</span>
                    ) : null}
                  </li>
                ))}
              </ol>
            </div>
            {version.sources.length > 0 ? (
              <div className="flex flex-col gap-2">
                <h2 className="text-sm font-semibold text-navy-900">Sources cited</h2>
                <ol className="flex flex-col gap-2 text-sm">
                  {version.sources.map((s) => (
                    <li key={s.n}>
                      <Link
                        to={`/library/${s.collection === 'cases' ? 'cases/' : ''}${s.versionId}?page=${s.pageStart}`}
                        className="text-navy-900 underline-offset-2 hover:underline"
                      >
                        [{s.n}] {displayTitle(s.title)} — {s.label}
                      </Link>
                    </li>
                  ))}
                </ol>
                {version.kind === 'edit' ? (
                  <p className="text-xs text-muted-600">
                    Citation numbers in hand-edited text are not checked.
                  </p>
                ) : null}
              </div>
            ) : null}
          </aside>
        </div>
      ) : null}
    </Page>
  );
}
